import { Suspense, lazy } from 'react';
import '@/gateway/devtools/bootTrace';
import { createRoot } from 'react-dom/client';
import { Provider as ReduxProvider } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { configureApiClient, createScopedApi, shouldRetryQuery } from '@clarix/api-client';
import { configureMasterConfigApi } from '@clarix/services-master-config';
import { useAuthStore, slugFromQuery } from '@clarix/auth-store';
import { registerQueryClient } from '@clarix/contexts';
import { clearPinnedSidebarPanel } from '@clarix/layout';
import App from './App';
import { env, IS_SINGLE_HOST, IS_DEV } from '@/config/env';
import { store } from './store';
import { bootstrapAuthFromHash } from '@/core/api';
import { installStaleChunkRecovery } from '@/gateway/staleChunkRecovery';
import { getWorkspaceSlug } from '@/gateway/getWorkspaceSlug';
import { workspaceLinkRedirect } from '@/gateway/workspaceLinkRedirect';
import './styles/globals.css';

// Registered before anything can import a lazy route or a federated remote, so a
// tab that was open across a deploy reloads onto the current build instead of
// crashing on chunk files the release replaced.
installStaleChunkRecovery();

// A mailed apex link carrying `?ws=<slug>` hops to that workspace's subdomain with
// the exact page intact, instead of landing on the workspace picker. First thing,
// before auth bootstrap or any render, so nothing flashes on the apex.
const workspaceHop = workspaceLinkRedirect();
if (workspaceHop) window.location.replace(workspaceHop);

// Publish this build's timestamp for @clarix/ui-shared's AppUpdateBanner, which
// compares it against the deployed /version.json to spot a newer release.
//
// The assignment is load-bearing, not a convenience. The banner reads
// `globalThis.__BUILD_TIME__` — a member expression — while Vite's `define`
// substitutes only the BARE identifier. Without this line the banner reads
// undefined, falls back to 0, and every `serverBuildTime > 0` comparison is
// true: a permanent "New version available" prompt on a build the user is
// already running. Here the right-hand side IS the bare identifier, so it
// compiles to the same literal that vite.config.ts wrote into version.json.
(globalThis as { __BUILD_TIME__?: number }).__BUILD_TIME__ = __BUILD_TIME__;

// `?force-logout=1` is set by UserMenu when the user signs out from another
// origin (e.g. a remote app on :5001). localStorage is per-origin, so the
// remote can't reach this origin's persisted auth — the shell has to clear
// its own. We do this BEFORE any data hooks run so RequireUnauthed sees the
// token as null and routes to /login instead of bouncing to the workspace.
(function consumeForceLogout() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('force-logout') !== '1') return;
  try {
    useAuthStore.getState().clear();
    localStorage.removeItem('clarix.auth.v1');
    // Session chrome goes with the session: a pinned sidebar panel must not
    // resurface over the next login's Home (mirrors useSignOut).
    clearPinnedSidebarPanel();
  } catch {
    /* ignore */
  }
  params.delete('force-logout');
  const search = params.toString();
  const next = `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`;
  window.history.replaceState(null, '', next);
})();

configureApiClient({ baseURL: env.VITE_API_BASE_URL, singleHost: IS_SINGLE_HOST });
// The Master rail panel renders the CRM master-config categories
// (`useCategories()`), which live under the `/crm` gateway prefix — not the
// gateway root the singleton points at. Back the shared
// `@clarix/services-master-config` hooks with a `/crm`-scoped client (same
// auth + payload encryption via createScopedApi → createApiClient) so the shell
// can fetch them without importing any CRM-remote internals.
configureMasterConfigApi(
  createScopedApi({ baseURL: `${env.VITE_API_BASE_URL.replace(/\/+$/, '')}/crm` }).apiService,
);
// If the URL contains #token=<jwt> (set by the producer on cross-subdomain
// redirects), pull it into this origin's auth store and strip the hash.
bootstrapAuthFromHash();

// `?workspace=<slug>` is set by sign-out (useSignOut / useLogout). On a
// single-host deployment the session is the ONLY place the workspace slug
// lives, so clearing it on sign-out takes the workspace with it and `/login`
// can then only ask "which workspace?" — for the workspace the user was
// already in. Adopting the slug back here makes LoginRoute render that
// workspace's sign-in form instead.
//
// Runs AFTER bootstrapAuthFromHash so a real session's slug always wins, and
// defers to the hostname on subdomain deployments, where the host is
// authoritative and this param is merely along for the ride. The param is left
// in the URL on purpose (see loginUrlForSlug) so a refresh still resolves.
(function adoptWorkspaceSlugFromQuery() {
  if (getWorkspaceSlug()) return;
  const fromUrl = slugFromQuery();
  if (!fromUrl) return;
  const auth = useAuthStore.getState();
  if (auth.workspaceSlug) return;
  auth.setWorkspaceSlug(fromUrl);
})();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: shouldRetryQuery },
  },
});

// One devtools panel for the whole ecosystem: each app publishes its own client
// to the registry and the hub switches between them. Lazy so a built bundle
// never requests the chunk.
if (IS_DEV) registerQueryClient({ id: 'shell', label: 'Shell (gateway)', client: queryClient });
const QueryDevtools = lazy(() =>
  import('@/gateway/devtools/QueryDevtoolsHub').then((m) => ({ default: m.QueryDevtoolsHub })),
);

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');

// No <StrictMode>: its dev-only double run of every effect doubled each Redux-thunk and
// useEffect fetch in every remote, so local network traffic no longer matched production.
createRoot(rootEl).render(
  <ReduxProvider store={store}>
    <QueryClientProvider client={queryClient}>
      <App />
      {IS_DEV && (
        <Suspense fallback={null}>
          <QueryDevtools />
        </Suspense>
      )}
    </QueryClientProvider>
  </ReduxProvider>,
);
